import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash, Percent, PencilSimple, DotsThreeVertical, Sparkle } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

const emptyForm = { party_id: "", charge_percent: "", product: "", notes: "", effective_from: "", min_slip_amount: "", flat_fee_below_min: "" };

export default function Pricing() {
    const [rules, setRules] = useState([]);
    const [parties, setParties] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        const [r1, r2] = await Promise.all([api.get("/pricing"), api.get("/parties")]);
        setRules(r1.data);
        setParties(r2.data);
    };
    useEffect(() => { load(); }, []);

    const partyName = (id) => parties.find((p) => p.id === id)?.name || "—";

    const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            party_id: r.party_id || "",
            charge_percent: r.charge_percent != null ? String(r.charge_percent) : "",
            product: r.product || "",
            notes: r.notes || "",
            effective_from: r.effective_from || new Date().toISOString().slice(0, 10),
            min_slip_amount: r.min_slip_amount ? String(r.min_slip_amount) : "",
            flat_fee_below_min: r.flat_fee_below_min ? String(r.flat_fee_below_min) : "",
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            const body = {
                party_id: form.party_id,
                charge_percent: Number(form.charge_percent),
                product: form.product || "",
                notes: form.notes || "",
                effective_from: form.effective_from || null,
                min_slip_amount: form.min_slip_amount === "" ? 0 : Number(form.min_slip_amount),
                flat_fee_below_min: form.flat_fee_below_min === "" ? 0 : Number(form.flat_fee_below_min),
            };
            let backfill = null;
            if (editing) {
                const { data } = await api.put(`/pricing/${editing.id}`, body);
                backfill = data.backfill;
                const scope = backfill?.from_date ? `from ${dmY(backfill.from_date)}` : "for all past payments";
                toast.success(
                    backfill?.created || backfill?.removed
                        ? `Rule updated · ${backfill.removed} old removed, ${backfill.created} recalculated ${scope}`
                        : "Rule updated"
                );
            } else {
                const { data } = await api.post("/pricing", body);
                backfill = data.backfill;
                toast.success(
                    backfill?.created
                        ? `Rule added · ${backfill.created} past payment${backfill.created > 1 ? "s" : ""} auto-charged @ ${backfill.charge_percent}%`
                        : "Rule added"
                );
            }
            setOpen(false);
            setForm(emptyForm);
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const applyRule = async (r) => {
        const hasFlat = r.min_slip_amount > 0 && r.flat_fee_below_min > 0;
        const rateLine = hasFlat
            ? `${r.charge_percent}% + flat ₹${r.flat_fee_below_min} for slips below ₹${r.min_slip_amount}`
            : `${r.charge_percent}%`;
        if (!window.confirm(
            `Recompute all charges for this party at ${rateLine}?\n\n` +
            `This wipes existing commission + portal-fee entries and re-adds fresh ones — useful for migrating legacy flat-only charges to the new split model.`
        )) return;
        try {
            const { data } = await api.post(`/pricing/${r.id}/apply`);
            toast.success(`Recomputed · ${data.created} new charges, ${data.removed} old removed @ ${data.charge_percent}%`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete rule?")) return;
        await api.delete(`/pricing/${id}`);
        toast.success("Deleted");
        load();
    };

    const applyAll = async () => {
        if (!window.confirm("Recompute commissions + portal fees for EVERY active party at their current pricing rule?\n\nThis wipes and rebuilds all commission + portal_fee txns. Safe — payments and payouts are untouched. Best-run once after a pricing model change or to migrate legacy flat-only rows.")) return;
        try {
            const { data } = await api.post("/pricing/recompute-all");
            toast.success(`Recomputed ${data.parties_touched} parties · +${data.total_created} created, -${data.total_removed} removed`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="pricing-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Configuration</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Pricing Rules</h1>
                    <p className="mt-2 text-sm text-slate-500">Percentage commission charged per party on verified payments.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button data-testid="recompute-all-btn" onClick={applyAll} variant="outline" className="border-slate-300">
                        <Sparkle size={16} className="mr-2 text-amber-500" /> Recompute all parties
                    </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-pricing-btn" onClick={openCreate} className="bg-blue-600 text-white hover:bg-blue-700">
                            <Plus size={16} className="mr-2" /> Add Rule
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-display">{editing ? "Edit Pricing Rule" : "New Pricing Rule"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={save} className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Party *</Label>
                                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                                    <SelectTrigger data-testid="pricing-party" className="mt-2"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent>
                                        {parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Charge % *</Label>
                                <div className="relative mt-2">
                                    <Input
                                        data-testid="pricing-percent"
                                        required
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={form.charge_percent}
                                        onChange={(e) => setForm({ ...form, charge_percent: e.target.value })}
                                        className="pr-9"
                                        placeholder="e.g. 1"
                                    />
                                    <Percent size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Product (optional)</Label>
                                <Input data-testid="pricing-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="mt-2" placeholder="e.g. Import Fee, General Trade" />
                            </div>

                            {/* Portal-charge override for small slips */}
                            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-amber-800">
                                    Portal charge for small slips (optional)
                                </div>
                                <div className="text-[11px] text-amber-800/80 mb-3">
                                    When a slip amount is below the threshold, charge a flat portal fee instead of the % rate. Leave both blank to disable.
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">If slip amount &lt;</Label>
                                        <div className="relative mt-1">
                                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                            <Input
                                                data-testid="pricing-min-amount"
                                                type="number"
                                                step="1"
                                                min="0"
                                                value={form.min_slip_amount}
                                                onChange={(e) => setForm({ ...form, min_slip_amount: e.target.value })}
                                                className="pl-6"
                                                placeholder="e.g. 5000"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Flat fee to charge</Label>
                                        <div className="relative mt-1">
                                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                            <Input
                                                data-testid="pricing-flat-fee"
                                                type="number"
                                                step="1"
                                                min="0"
                                                value={form.flat_fee_below_min}
                                                onChange={(e) => setForm({ ...form, flat_fee_below_min: e.target.value })}
                                                className="pl-6"
                                                placeholder="e.g. 25"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {editing && (
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Effective from</Label>
                                    <Input
                                        data-testid="pricing-effective-from"
                                        type="date"
                                        value={form.effective_from}
                                        onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                                        className="mt-2"
                                    />
                                    <div className="mt-1 text-xs text-slate-500">
                                        Payments before this date keep their existing commission at the old rate.
                                        Leave blank to re-apply the new rate to <b>all</b> past payments.
                                    </div>
                                </div>
                            )}
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Notes</Label>
                                <Input data-testid="pricing-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button type="submit" data-testid="save-pricing-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                                    {editing ? "Update Rule" : "Add Rule"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {rules.length === 0 ? (
                <EmptyState title="No pricing rules" description="Configure the percentage you charge per party." />
            ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="pricing-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                    <th className="px-5 py-3 font-medium">Party</th>
                                    <th className="px-5 py-3 text-right font-medium">Charge %</th>
                                    <th className="px-5 py-3 font-medium">Portal charge (if slip &lt;)</th>
                                    <th className="px-5 py-3 font-medium">Product</th>
                                    <th className="px-5 py-3 font-medium">Notes</th>
                                    <th className="px-5 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-5 py-3 text-slate-900">{partyName(r.party_id)}</td>
                                        <td className="px-5 py-3 text-right tabular-nums font-medium text-slate-900">
                                            {r.charge_percent != null ? `${Number(r.charge_percent).toFixed(2)} %` : "—"}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-700" data-testid={`pricing-portal-charge-${r.id}`}>
                                            {r.min_slip_amount > 0 && r.flat_fee_below_min > 0
                                                ? <span>&lt; ₹{Number(r.min_slip_amount).toLocaleString("en-IN")} <span className="text-slate-500">→</span> flat ₹{Number(r.flat_fee_below_min).toLocaleString("en-IN")}</span>
                                                : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="px-5 py-3 text-slate-700">{r.product || "—"}</td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{r.notes || "—"}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex justify-end">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            data-testid={`pricing-actions-${r.id}`}
                                                            aria-label="Rule actions"
                                                            className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                                                        >
                                                            <DotsThreeVertical size={18} weight="bold" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-56">
                                                        <DropdownMenuItem
                                                            data-testid={`menu-edit-pricing-${r.id}`}
                                                            onClick={() => openEdit(r)}
                                                            className="cursor-pointer"
                                                        >
                                                            <PencilSimple size={16} className="mr-2 text-slate-500" />
                                                            Edit rule
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            data-testid={`menu-apply-pricing-${r.id}`}
                                                            onClick={() => applyRule(r)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Sparkle size={16} className="mr-2 text-slate-500" />
                                                            Recompute all charges
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            data-testid={`menu-delete-pricing-${r.id}`}
                                                            onClick={() => remove(r.id)}
                                                            className="cursor-pointer text-rose-600 focus:text-rose-700"
                                                        >
                                                            <Trash size={16} className="mr-2" />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
